/**
 * Нормализация данных игроков/рейтинга (CONTRACT §2) с безопасными значениями по умолчанию,
 * чтобы UI работал и со старым bunker (без новых полей).
 *
 * PlayerInfo (нормализованный):
 *   { userId:number|null, playerName:string, countWins:number, countLosses:number,
 *     gamesPlayed:number, stars:number, avatarUrl:string|null, customAvatar:boolean, username:string|null }
 *   customAvatar — avatarUrl загружен игроком (а не фото из Telegram)
 *
 * RatingRow (нормализованный):
 *   { rank:number|null, userId:number|null, playerName:string, wins:number,
 *     gamesPlayed:number, avatarUrl:string|null }
 */

export const DEFAULT_PLAYER_NAME = "Игрок";

const toInt = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
};

const toIdOrNull = (value) => (value === null || value === undefined || value === "" ? null : toInt(value, null));

/** Нормализует PlayerInfo с сервера. null/undefined → null. */
export function normalizePlayer(raw) {
  if (!raw || typeof raw !== "object") return null;
  const wins = toInt(raw.countWins);
  const losses = toInt(raw.countLosses);
  return {
    userId: toIdOrNull(raw.userId),
    playerName: (raw.playerName && String(raw.playerName).trim()) || DEFAULT_PLAYER_NAME,
    countWins: wins,
    countLosses: losses,
    gamesPlayed: raw.gamesPlayed === undefined || raw.gamesPlayed === null ? wins + losses : toInt(raw.gamesPlayed),
    stars: toInt(raw.stars),
    avatarUrl: raw.avatarUrl || null,
    customAvatar: raw.customAvatar === true,
    username: raw.username || null,
  };
}

/** Нормализует строку рейтинга. index — позиция в списке (для старого API без rank). */
export function normalizeRatingRow(raw, index = null) {
  if (!raw || typeof raw !== "object") return null;
  const rank = raw.rank === null || raw.rank === undefined ? (index === null ? null : index + 1) : toInt(raw.rank, null);
  return {
    rank,
    userId: toIdOrNull(raw.userId),
    playerName: (raw.playerName || raw.winner || DEFAULT_PLAYER_NAME).toString(),
    wins: toInt(raw.wins),
    gamesPlayed: toInt(raw.gamesPlayed, toInt(raw.wins)),
    avatarUrl: raw.avatarUrl || null,
  };
}

/** Инициалы для аватара-заглушки: «Ваня777» → «В», «Олег Бодрый» → «ОБ». */
export function initialsOf(name) {
  const words = String(name || DEFAULT_PLAYER_NAME)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return "?";
  const letters = words.length > 1 ? words[0][0] + words[1][0] : words[0][0];
  return letters.toUpperCase();
}

/** Детерминированный хэш строки (для цвета аватара). */
export function hashString(value) {
  const str = String(value || "");
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

/** CSS-градиент для аватара-заглушки, стабильный для одного имени. */
export function avatarGradient(seed) {
  const hash = hashString(seed);
  const hue = 55 + (hash % 110); // от жёлто-лаймового до зелёного — в палитре приложения
  const hue2 = hue + 20 + (hash % 40);
  return `linear-gradient(135deg, hsl(${hue} 48% 42%) 0%, hsl(${hue2} 42% 16%) 100%)`;
}

/**
 * Ссылка-приглашение (CONTRACT §6): t.me/<bot>?startapp=ref_<id>, если задан VUE_APP_BOT_USERNAME,
 * иначе <origin>/?ref=<id>.
 */
export function inviteLink(userId) {
  const bot = (process.env.VUE_APP_BOT_USERNAME || "").trim().replace(/^@/, "");
  const id = userId === null || userId === undefined ? "" : String(userId);
  if (bot) return `https://t.me/${bot}?startapp=ref_${id}`;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/?ref=${id}`;
}

/** Нормализует режим игры: всё, кроме 'MOUTH', считается 'BLINK'. */
export function normalizeMode(mode) {
  return String(mode || "").toUpperCase() === "MOUTH" ? "MOUTH" : "BLINK";
}

/** Подписи режимов игры. */
export const MODE_LABELS = Object.freeze({
  BLINK: "Моргание",
  MOUTH: "Открытый рот",
});
